/**
 * Sidebar DOM Manipulation
 *
 * Utility functions for manipulating sidebar-related DOM elements.
 * These functions handle CSS class changes for sidebar width, visibility,
 * and layout transitions.
 */

import { BOTTOM_MENU_CLASSES, MAIN_CONTENT_MARGIN_CLASSES, SIDEBAR_WIDTH_CLASSES } from "./config";

// ============================================
// Sidebar Width Classes
// ============================================

/**
 * Updates the sidebar element's width classes for expanded or mini mode.
 *
 * Args:
 *   sidebar: The sidebar HTMLElement.
 *   isExpanded: True for expanded mode (lg:w-72), false for mini mode (lg:w-16).
 */
export function updateSidebarWidthClasses(sidebar: HTMLElement, isExpanded: boolean): void {
  if (isExpanded) {
    sidebar.classList.add(SIDEBAR_WIDTH_CLASSES.EXPANDED);
    sidebar.classList.remove(SIDEBAR_WIDTH_CLASSES.MINI);
  } else {
    sidebar.classList.remove(SIDEBAR_WIDTH_CLASSES.EXPANDED);
    sidebar.classList.add(SIDEBAR_WIDTH_CLASSES.MINI);
  }
}

/**
 * Updates the main content element's margin classes for sidebar state.
 *
 * Args:
 *   mainContent: The main content HTMLElement, or null.
 *   isExpanded: True for expanded margin (lg:ml-72), false for mini margin (lg:ml-16).
 */
export function updateMainContentMargin(
  mainContent: HTMLElement | null,
  isExpanded: boolean,
): void {
  if (!mainContent) return;

  if (isExpanded) {
    mainContent.classList.add(MAIN_CONTENT_MARGIN_CLASSES.EXPANDED);
    mainContent.classList.remove(MAIN_CONTENT_MARGIN_CLASSES.MINI);
  } else {
    mainContent.classList.remove(MAIN_CONTENT_MARGIN_CLASSES.EXPANDED);
    mainContent.classList.add(MAIN_CONTENT_MARGIN_CLASSES.MINI);
  }
}

// ============================================
// Toggle Item Visibility
// ============================================

/**
 * Updates visibility of sidebar toggle items (text labels, icons, etc.).
 *
 * Elements with the [sidebar-toggle-item] attribute are shown in expanded
 * mode and hidden in mini mode.
 *
 * Args:
 *   sidebar: The sidebar HTMLElement.
 *   isExpanded: True to show items, false to hide them.
 */
export function updateToggleItems(sidebar: HTMLElement, isExpanded: boolean): void {
  const toggleItems = sidebar.querySelectorAll<HTMLElement>("[sidebar-toggle-item]");

  toggleItems.forEach((item) => {
    if (isExpanded) {
      item.classList.remove("lg:hidden");
      item.classList.remove("lg:absolute");
    } else {
      item.classList.add("lg:hidden");
      item.classList.add("lg:absolute");
    }
  });
}

// ============================================
// Nested Menu Item Text
// ============================================

/**
 * Updates nested menu items to show full text or first letter abbreviation.
 *
 * In expanded mode, shows full text. In mini mode, shows single letter.
 * Also updates padding classes for proper alignment.
 *
 * Args:
 *   sidebar: The sidebar HTMLElement.
 *   isExpanded: True for full text, false for abbreviated.
 */
export function updateNestedMenuItems(sidebar: HTMLElement, isExpanded: boolean): void {
  const nestedLinks = sidebar.querySelectorAll<HTMLAnchorElement>("ul > li > ul > li > a");

  nestedLinks.forEach((link) => {
    const fullTextEl = link.children[0] as HTMLElement | undefined;
    const firstLetterEl = link.children[1] as HTMLElement | undefined;

    if (isExpanded) {
      link.classList.add("pl-11");
      link.classList.remove("px-4");

      if (fullTextEl) {
        fullTextEl.classList.remove("lg:hidden");
      }
      if (firstLetterEl) {
        firstLetterEl.classList.remove("lg:inline");
      }
    } else {
      link.classList.remove("pl-11");
      link.classList.add("px-4");

      if (fullTextEl) {
        fullTextEl.classList.add("lg:hidden");
      }
      if (firstLetterEl) {
        firstLetterEl.classList.add("lg:inline");
      }
    }
  });
}

/**
 * Initializes nested menu items with full text and first letter spans.
 *
 * This prepares nested links for toggle between full text and abbreviation
 * by wrapping the text content in two span elements.
 *
 * Args:
 *   sidebar: The sidebar HTMLElement.
 */
export function initializeNestedMenuItems(sidebar: HTMLElement): void {
  const nestedLinks = sidebar.querySelectorAll<HTMLAnchorElement>("ul > li > ul > li > a");

  nestedLinks.forEach((link) => {
    const fullText = link.textContent || "";
    const firstLetter = fullText.substring(0, 1);

    const fullTextEl = document.createElement("span");
    const firstLetterEl = document.createElement("span");
    firstLetterEl.classList.add("hidden");
    fullTextEl.textContent = fullText;
    firstLetterEl.textContent = firstLetter;

    link.textContent = "";
    link.appendChild(fullTextEl);
    link.appendChild(firstLetterEl);
  });
}

// ============================================
// Bottom Menu Layout
// ============================================

/**
 * Updates the bottom menu layout classes for expanded or mini mode.
 *
 * Args:
 *   bottomMenu: The bottom menu HTMLElement, or null.
 *   isExpanded: True for horizontal layout, false for vertical layout.
 */
export function updateBottomMenuLayout(bottomMenu: HTMLElement | null, isExpanded: boolean): void {
  if (!bottomMenu) return;

  if (isExpanded) {
    // Remove mini mode classes
    BOTTOM_MENU_CLASSES.MINI.forEach((cls) => bottomMenu.classList.remove(cls));
    // Add expanded mode classes
    BOTTOM_MENU_CLASSES.EXPANDED.forEach((cls) => bottomMenu.classList.add(cls));
  } else {
    // Remove expanded mode classes
    BOTTOM_MENU_CLASSES.EXPANDED.forEach((cls) => bottomMenu.classList.remove(cls));
    // Add mini mode classes
    BOTTOM_MENU_CLASSES.MINI.forEach((cls) => bottomMenu.classList.add(cls));
  }
}

// ============================================
// Mobile Sidebar Toggle
// ============================================

/**
 * Toggles the mobile sidebar visibility.
 *
 * Args:
 *   sidebar: The sidebar HTMLElement.
 *   backdrop: The backdrop overlay element, or null.
 *   hamburgerIcon: The hamburger menu icon element, or null.
 *   closeIcon: The close icon element, or null.
 */
export function toggleSidebarMobile(
  sidebar: HTMLElement,
  backdrop: HTMLElement | null,
  hamburgerIcon: HTMLElement | null,
  closeIcon: HTMLElement | null,
): void {
  sidebar.classList.toggle("hidden");
  backdrop?.classList.toggle("hidden");
  hamburgerIcon?.classList.toggle("hidden");
  closeIcon?.classList.toggle("hidden");
}

// ============================================
// Element Queries
// ============================================

/**
 * Gets all sidebar-related elements.
 *
 * Returns:
 *   Object with sidebar elements, any of which may be null if not found.
 */
export function getSidebarElements() {
  return {
    sidebar: document.getElementById("sidebar"),
    toggleButton: document.getElementById("toggleSidebar"),
    mainContent: document.getElementById("main-content"),
    bottomMenu: document.querySelector<HTMLElement>("[sidebar-bottom-menu]"),
    backdrop: document.getElementById("sidebarBackdrop"),
    mobileToggle: document.getElementById("toggleSidebarMobile"),
    mobileHamburger: document.getElementById("toggleSidebarMobileHamburger"),
    mobileClose: document.getElementById("toggleSidebarMobileClose"),
    mobileSearch: document.getElementById("toggleSidebarMobileSearch"),
  };
}

/**
 * Removes the no-transition class from elements after initial load.
 *
 * This allows smooth transitions after the initial render without
 * the jarring animation on page load.
 *
 * Args:
 *   elements: Array of HTMLElements to enable transitions on.
 *   delay: Delay in milliseconds before removing the class.
 */
export function enableTransitionsAfterLoad(elements: (HTMLElement | null)[], delay = 50): void {
  setTimeout(() => {
    for (const el of elements) {
      if (el) {
        el.classList.remove("no-transition");
      }
    }
  }, delay);
}
